import { t } from "i18next";
import { useState, useEffect, useCallback } from "react";
import { IconButton, Typography } from "@material-tailwind/react";
import { Table } from "../../../components/table/Table";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import EditCreateDrawer from "../../../components/edit-create-drawer/EditCreateDrawer";
import ContactForm, { ContactFormValidator } from "../../../components/data/contact/ContactForm";
import { GenericListLayout } from "../../../components/generic-list-view/GenericListLayout";
import FilterDrawer from "../../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../../components/filter/FiltersDisplay";
import { Pagination } from "../../../components/pagination/Pagination";
import { validator } from "../../../services/form-validator/form-validator";
import { api } from "../../../services/api/api";
import toast from "react-hot-toast";
import DeleteContactModal from "./modals/DeleteContactModal";
import { TrashIcon } from "@heroicons/react/24/outline";
import Loading from "../../../components/loading/Loading";
import { useTablePageSize } from "../../../hooks/useTablePageSize";

export const getContactEmails = (contact?: EasyTech.Contact | null): string[] => {
  if (!contact?.contactInfo) return [];
  const emails: string[] = [];
  const rawEmails = contact.contactInfo.emails;
  if (Array.isArray(rawEmails)) {
    rawEmails.forEach((e) => {
      if (typeof e === "string" && e.trim() && !emails.includes(e.trim())) {
        emails.push(e.trim());
      }
    });
  }
  const single = contact.contactInfo.email || contact.contactInfo.mail;
  if (typeof single === "string" && single.trim() && !emails.includes(single.trim())) {
    emails.push(single.trim());
  }
  return emails;
};

const ContactFilterForm = ({
  data,
  onChange,
  onSubmit,
  submitted,
}: {
  data?: any;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}) => {
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      onChange(parent, { [child]: value });
    } else {
      onChange(name, value);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("common:firstName")}
        </label>
        <input
          type="text"
          name="firstName"
          value={(data?.firstName as string) || ""}
          onChange={handleFieldChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("common:lastName")}
        </label>
        <input
          type="text"
          name="lastName"
          value={(data?.lastName as string) || ""}
          onChange={handleFieldChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("common:function")}
        </label>
        <input
          type="text"
          name="function"
          value={(data?.function as string) || ""}
          onChange={handleFieldChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("common:email")}
        </label>
        <input
          type="text"
          name="contactInfo.email"
          value={data?.contactInfo?.email || ""}
          onChange={handleFieldChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("common:phone")}
        </label>
        <input
          type="text"
          name="contactInfo.phone"
          value={data?.contactInfo?.phone || ""}
          onChange={handleFieldChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>
    </div>
  );
};

/**
 * Contacts Tab Component
 * Affiche la liste des contacts pour un client spécifique
 */
const ContactsTab = ({
  data,
  onContactCreate,
  onContactUpdated,
  onContactDeleted,
}: {
  data: EasyTech.Customer;
  onContactCreate?: (data: EasyTech.Contact) => Promise<void>;
  onContactUpdated?: (contact: EasyTech.Contact) => void;
  onContactDeleted?: (contactId: string) => void;
}) => {
  const [contacts, setContacts] = useState<EasyTech.Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<EasyTech.Contact[]>([]);
  const [pageState, setPageState] = useState<number>(0);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<any>({});
  // Contact ciblé par la suppression. Un simple booléen partagé montait une modale
  // par ligne du tableau, et c'est la dernière ligne rendue qui passait au-dessus.
  const [contactToDelete, setContactToDelete] = useState<EasyTech.Contact | null>(null);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const applyFilters = useCallback(
    (contactsList: EasyTech.Contact[], currentFilters: any) => {
      if (!contactsList) return;

      let results = [...contactsList];

      // Si aucun filtre n'est appliqué, on affiche tous les contacts
      if (Object.keys(currentFilters).length === 0) {
        const paginatedContacts = results.slice(
          pageState * pageSize,
          (pageState + 1) * pageSize
        );
        setFilteredContacts(paginatedContacts);
        setPageNumberState(Math.ceil(results.length / pageSize));
        return;
      }

      // Appliquer les filtres
      if (currentFilters.firstName) {
        results = results.filter((contact) =>
          contact.firstName
            .toLowerCase()
            .includes(currentFilters.firstName.toLowerCase())
        );
      }

      if (currentFilters.lastName) {
        results = results.filter((contact) =>
          contact.lastName
            .toLowerCase()
            .includes(currentFilters.lastName.toLowerCase())
        );
      }

      if (currentFilters.function) {
        results = results.filter(
          (contact) =>
            contact.function &&
            contact.function
              .toLowerCase()
              .includes(currentFilters.function.toLowerCase())
        );
      }

      if (currentFilters.contactInfo?.email) {
        const search = currentFilters.contactInfo.email.toLowerCase();
        results = results.filter((contact) =>
          getContactEmails(contact).some((email) => email.toLowerCase().includes(search))
        );
      }

      if (currentFilters.contactInfo?.phone) {
        results = results.filter(
          (contact) =>
            contact.contactInfo?.phone &&
            contact.contactInfo.phone.includes(currentFilters.contactInfo.phone)
        );
      }

      // Pagination des résultats filtrés
      const paginatedContacts = results.slice(
        pageState * pageSize,
        (pageState + 1) * pageSize
      );

      setFilteredContacts(paginatedContacts);
      setPageNumberState(Math.ceil(results.length / pageSize));
    },
    [pageState, pageSize]
  );

  // Récupérer les contacts du client
  useEffect(() => {
    if (data && data.id) {
      setIsLoading(true);
      // Utiliser directement les contacts depuis l'objet customer
      if (data.contacts && Array.isArray(data.contacts)) {
        setContacts(data.contacts);
        applyFilters(data.contacts, filters);
        setIsLoading(false);
      } else {
        setContacts([]);
        setFilteredContacts([]);
        setIsLoading(false);
      }
    }
  }, [data, filters, applyFilters]);

  useEffect(() => {
    applyFilters(contacts, filters);
  }, [contacts, filters, pageState, applyFilters]);

  // Gérer la soumission du filtre
  const handleFilterSubmit = (filterData: any): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFilters(filterData || {});
      setPageState(0);
      resolve();
    });
  };

  // Effacer tous les filtres
  const clearAllFilters = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFilters({});
      setPageState(0);
      resolve();
    });
  };

  // Transformer les filtres pour l'affichage
  const transformFiltersForDisplay = (
    originalFilters: any
  ): Record<string, any> => {
    const displayFilters: Record<string, any> = {};

    if (originalFilters.firstName)
      displayFilters.firstName = originalFilters.firstName;
    if (originalFilters.lastName)
      displayFilters.lastName = originalFilters.lastName;
    if (originalFilters.function)
      displayFilters.function = originalFilters.function;

    if (originalFilters.contactInfo) {
      if (originalFilters.contactInfo.email) {
        displayFilters.email = originalFilters.contactInfo.email;
      }
      if (originalFilters.contactInfo.phone) {
        displayFilters.phone = originalFilters.contactInfo.phone;
      }
    }

    return displayFilters;
  };

  // Effacer un filtre spécifique
  const handleFilterClear = (field: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const newFilters = { ...filters };

      if (field === "email" || field === "phone") {
        if (newFilters.contactInfo) {
          const newContactInfo = { ...newFilters.contactInfo };
          delete newContactInfo[field];

          if (Object.keys(newContactInfo).length === 0) {
            delete newFilters.contactInfo;
          } else {
            newFilters.contactInfo = newContactInfo;
          }
        }
      } else {
        delete newFilters[field];
      }

      setFilters(newFilters);
      setPageState(0);
      resolve();
    });
  };

  const handleContactEdit = (contact: EasyTech.Contact): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(contact, ContactFormValidator)) {
        api.put("users/contacts/"+contact.id, {
          ...contact,
          customer: { id: data.id }
        })
          .then((ret) => {
            const saved = (ret && typeof ret === "object" ? ret : {}) as EasyTech.Contact;
            const updated: EasyTech.Contact = { ...contact, ...saved, id: contact.id };
            setContacts((prev) => prev.map((c) => (c.id === contact.id ? updated : c)));
            onContactUpdated?.(updated);
            toast.success(t("Contact edited successfully"));
            return resolve();
          }).catch((err) => {
            console.error(err);
            toast.error(t("An error occurred while adding the machine"));
            return reject(err);
          });
      } else {
        const fieldNames = {
          firstName: t("common:firstName"),
          lastName: t("common:lastName"),
          function: t("common:function"),
          civility: t("common:civility"),
          email: t("common:email"),
          phone: t("common:phone"),
          fax: t("common:fax"),
          notes: t("common:notes")
        };

        const errorMessage = validator.getFormErrorMessage(contact, ContactFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };
  
  // Actions d'en-tête
  const headerActions = (
    <EditCreateDrawer<EasyTech.Contact>
      entityName="contact"
      FormComponent={ContactForm}
      onSubmit={
        onContactCreate ||
        ((contactData) => {
          return new Promise<void>((resolve, reject) => {
            reject();
          });
        })
      }
      isNotCreatable={false}
      isNotEditable={true}
    />
  );

  // Composant du drawer de filtre
  const filterDrawerComponent = (
    <FilterDrawer
      FormComponent={ContactFilterForm}
      onSubmit={handleFilterSubmit}
      entityName="Contacts"
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={transformFiltersForDisplay(filters)}
      onClearFilter={handleFilterClear}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
    />
  );

  const tableHead = [
    { label: t("common:firstName"), orderKey: "firstName", width: "15%" },
    { label: t("common:lastName"), orderKey: "lastName", width: "15%" },
    { label: t("common:function"), orderKey: "function", width: "15%" },
    { label: t("common:phone"), orderKey: "contactInfo.phone", width: "15%" },
    { label: t("common:email"), orderKey: "contactInfo.mail", width: "20%" },
    { label: t("common:actions"), orderKey: null, width: "15%" },
  ];

  const content = (
    <Table
      tableHead={tableHead}
      emptyTable={filteredContacts.length === 0 && t("contact:noData")}
      enableRowLinks={true}
    >
      {filteredContacts.map((contact, index) => {
        const isLast = index === filteredContacts.length - 1;
        const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";

        return (
          <tr key={contact.id}>
            <td className={classes}>
              <OverflowTooltip content={contact.firstName || ""} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {contact.firstName}
                </Typography>
              </OverflowTooltip>
            </td>

            <td className={classes}>
              <OverflowTooltip content={contact.lastName || ""} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {contact.lastName}
                </Typography>
              </OverflowTooltip>
            </td>

            <td className={classes}>
              <OverflowTooltip content={contact.function || ""} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {contact.function}
                </Typography>
              </OverflowTooltip>
            </td>

            <td className={classes}>
              <OverflowTooltip content={contact.contactInfo?.phone || t("common:noPhone")} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {contact.contactInfo?.phone || t("common:noPhone")}
                </Typography>
              </OverflowTooltip>
            </td>

            <td className={classes}>
              {(() => {
                const emails = getContactEmails(contact);
                if (emails.length === 0) {
                  return (
                    <OverflowTooltip content={t("common:noEmail")} className="et-cell-truncate">
                      <Typography variant="small" color="blue-gray">
                        {t("common:noEmail")}
                      </Typography>
                    </OverflowTooltip>
                  );
                }

                return (
                  <div className="flex flex-col gap-0.5">
                    {emails.map((email, emailIdx) => (
                      <OverflowTooltip key={emailIdx} content={email} className="et-cell-truncate">
                        <Typography variant="small" color="blue-gray">
                          {email}
                        </Typography>
                      </OverflowTooltip>
                    ))}
                  </div>
                );
              })()}
            </td>

            <td className={classes}> 
              <div className="et-cell-actions">
                <EditCreateDrawer<EasyTech.Contact>
                  entityName="Contact"
                  data={contact}
                  FormComponent={ContactForm}
                  onSubmit={handleContactEdit}
                  isNotEditable={false}
                  isNotCreatable={true}
                />
                {contactToDelete?.id === contact.id ?
                  <Loading size="h-4 w-4" />
                :
                  <IconButton onClick={() => setContactToDelete(contact)} 
                    variant="text"
                    className={"icon-btn"} 
                    size="sm">
                    <TrashIcon className="w-[14px]"  />
                  </IconButton>
                }
              </div>
            </td>
          </tr>
        );
      })}
    </Table>
  );

  const paginationComponent =
    filteredContacts && filteredContacts.length > 0 ? (
      <Pagination
        actualPage={pageState}
        pageNumber={pageNumberState}
        changePage={setPageState}
      />
    ) : null;

  return (
    <>
      <GenericListLayout
        entityName={t("common:contacts")}
        isLoading={isLoading}
        headerActions={headerActions}
        filterDrawer={filterDrawerComponent}
        filterBadges={filterBadgesComponent}
        content={content}
        pagination={paginationComponent}
        showBreadcrumb={false}
      />

      {contactToDelete &&
        <DeleteContactModal open={true} contact={contactToDelete}
          onDelete={() => {
            const deletedId = contactToDelete.id;
            setContacts((prev) => prev.filter((c) => c.id !== deletedId));
            setPageState(0);
            onContactDeleted?.(deletedId);
          }}
          onClose={() => setContactToDelete(null)} />
      }
    </>
  );
};

export default ContactsTab;
